// Progressive Builder — React port of kula-create's progressive-builder.html.
//
// Pedagogy: layer-by-layer construction. The learner adds layers one at a
// time; the cumulative output evolves visibly with each addition. The
// transformation IS the learning.
//
// Single-row capture on completion:
//   { layersCompleted: string[], drafts?: Record<string,string>, reflection?: string }.

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ProgressiveBuilderSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { TerraProgressiveBuilder } from './TerraProgressiveBuilder';

interface Props {
  data: ProgressiveBuilderSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ProgressiveBuilderSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  /** Called exactly once when the learner submits the finished build. */
  onComplete?: () => void;
  /** Last recorded response payload for this slide — restores the submitted view. */
  savedResponse?: Record<string, unknown> | null;
}

export function ProgressiveBuilderSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Terra" theme world (flagship pairing).
  if (theme.id === 'terra') {
    return <TerraProgressiveBuilder data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ProgressiveBuilderSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // The payload ({layersCompleted, drafts, reflection}) only fires on submit, so
  // its presence means the build finished — restore the completed build +
  // submitted view from it.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.layersCompleted);

  const [completedLayerIds, setCompletedLayerIds] = useState<string[]>(() =>
    restored ? [...(saved.layersCompleted as string[])] : [],
  );
  // The learner's OWN contribution per layer — the active construction the
  // review §8 says was missing (it was click-and-see). Their drafts are the
  // build; the authored transformedOutput becomes a "strong version" to compare.
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    restored && saved.drafts && typeof saved.drafts === 'object'
      ? { ...(saved.drafts as Record<string, string>) }
      : {},
  );
  const [currentDraft, setCurrentDraft] = useState('');
  const [reflection, setReflection] = useState(
    restored && typeof saved.reflection === 'string' ? saved.reflection : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  const completedRef = useRef(restored);

  const currentLayerIdx = completedLayerIds.length;
  const allDone = currentLayerIdx >= data.layers.length && data.layers.length > 0;
  const hasReflectionPrompt = (data.reflection?.prompt ?? '').trim().length > 0;

  const currentOutput = allDone
    ? data.layers[data.layers.length - 1]?.transformedOutput ?? ''
    : currentLayerIdx === 0
      ? data.baseState
      : data.layers[currentLayerIdx - 1]?.transformedOutput ?? '';

  const addLayer = useCallback(() => {
    const next = data.layers[currentLayerIdx];
    if (!next || !currentDraft.trim()) return;
    setDrafts((prev) => ({ ...prev, [next.id]: currentDraft.trim() }));
    setCompletedLayerIds((prev) => [...prev, next.id]);
    setCurrentDraft('');
  }, [data.layers, currentLayerIdx, currentDraft]);

  const submit = useCallback(() => {
    setSubmitted(true);
    const responseData: Record<string, unknown> = { layersCompleted: completedLayerIds, drafts };
    // Only include reflection when the author actually set a prompt.
    if (hasReflectionPrompt) responseData.reflection = reflection;
    onActivityResponse?.({
      activityType: 'progressive-builder',
      mode: 'survey',
      responseData,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [completedLayerIds, drafts, reflection, hasReflectionPrompt, onActivityResponse, onComplete]);

  // Close: the learner's own layer-by-layer build, shown back.
  const draftEchoes = data.layers
    .filter((l) => drafts[l.id])
    .map((l) => ({ label: l.label, value: drafts[l.id] }));
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'What you built',
    body: 'You built this up one layer at a time. Compare your version against the strong version above.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Layer indicators */}
        <div className="flex items-center justify-center gap-1 mb-6">
          {data.layers.map((layer, idx) => {
            const isDone = idx < currentLayerIdx;
            const isCurrent = idx === currentLayerIdx && !allDone;
            return (
              <div
                key={layer.id}
                className="px-2 py-1 rounded text-xs font-semibold transition-colors duration-300"
                style={{
                  background: isCurrent ? accent : isDone ? `${accent}25` : theme.colors.surface,
                  color: isCurrent ? '#fff' : isDone ? accent : theme.colors.textMuted,
                }}
              >
                {idx + 1}. {layer.label}
              </div>
            );
          })}
        </div>

        {/* Cumulative output */}
        {data.layers.length === 0 ? (
          <div
            className="rounded-lg p-6 text-center text-sm"
            style={{ background: theme.colors.surface, color: theme.colors.textMuted }}
          >
            This builder has no layers yet — there is nothing to build.
          </div>
        ) : (
          /* Cumulative output — the hero. Re-keyed by content so each layer
             addition morphs in (fade + layout) instead of swapping instantly. */
          <Reveal
            layout
            variant="fade"
            className="rounded-lg p-5 mb-6 border-2"
            style={{ background: theme.colors.surface, borderColor: accent }}
          >
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
              {currentLayerIdx === 0 ? 'Starting point' : 'A strong version so far'}
            </p>
            {/* Keyed by content so each layer addition remounts and fades the new
                cumulative output in. NB: do NOT wrap in <AnimatePresence
                mode="wait"> — Reveal is a plain (non-forwardRef) component, so
                the exiting child never signals completion and the next output
                never mounts, freezing the card blank. */}
            <Reveal
              key={currentOutput}
              variant="fade"
              className="leading-relaxed text-sm whitespace-pre-wrap"
              style={{ color: theme.colors.text }}
            >
              {currentOutput || '(empty)'}
            </Reveal>
          </Reveal>
        )}

        {/* Next layer to add — keyed by layer index so advancing remounts and
            animates the new layer in. No <AnimatePresence mode="wait">: Reveal
            is a plain (non-forwardRef) component, so the exiting layer would
            block the next from mounting and freeze the panel blank. */}
        {!allDone && data.layers[currentLayerIdx] && (
            <Reveal
              key={currentLayerIdx}
              variant="up"
              className="rounded-lg p-5 mb-4 border"
              style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
            >
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                Your turn: {data.layers[currentLayerIdx].label}
              </p>
              <p className="text-sm mb-3" style={{ color: theme.colors.text }}>
                {data.layers[currentLayerIdx].addition}
              </p>
              {/* The learner writes THIS layer themselves — the construction the
                  type is built on. They compare against the strong version after. */}
              <textarea
                value={currentDraft}
                onChange={(e) => setCurrentDraft(e.target.value)}
                rows={2}
                placeholder="Write your version of this layer…"
                className="w-full px-3 py-2 rounded border text-sm mb-3"
                style={{
                  background: theme.colors.background,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
              <button
                onClick={addLayer}
                disabled={!currentDraft.trim()}
                className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                style={{
                  background: accent,
                  color: '#fff',
                  opacity: currentDraft.trim() ? 1 : 0.4,
                  cursor: currentDraft.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Add my layer
              </button>
            </Reveal>
          )}

        {/* Finish / reflection — a finish affordance ALWAYS renders once the
            build is done (audit finish-gate fix). When the author set a
            reflection prompt we show it in the payoff register (heavier accent
            rule + larger radius) and the CTA reads "Submit"; otherwise the same
            card carries a plain "Finish" button so the build can always be
            completed and onComplete/onActivityResponse always fire. */}
        <AnimatePresence>
          {allDone && !submitted && (
            <Reveal
              variant="scale"
              spring="bouncy"
              className="rounded-2xl p-5 border-l-[5px] shadow-sm"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              {hasReflectionPrompt && (
                <>
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Reflect</p>
                  <p className="text-sm mb-3" style={{ color: theme.colors.text }}>{data.reflection.prompt}</p>
                  <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded border text-sm mb-3"
                    style={{
                      background: theme.colors.background,
                      borderColor: `${theme.colors.textMuted}33`,
                      color: theme.colors.text,
                    }}
                  />
                </>
              )}
              <button
                onClick={submit}
                className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                style={{ background: accent, color: '#fff' }}
              >
                {hasReflectionPrompt ? 'Submit' : 'Finish'}
              </button>
            </Reveal>
          )}
        </AnimatePresence>

        {/* Authored close — shows the learner's own layer-by-layer build back
            (their drafts) + the "So what?". The before/after contrast is the
            payoff (review §8). */}
        <AnimatePresence>
          {submitted && (
            <Reveal variant="up" className="mt-4">
              <ExperienceClose conclusion={conclusion} theme={theme} echoes={draftEchoes} />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ProgressiveBuilderSlideData;
  theme: Theme;
  onUpdate: (data: ProgressiveBuilderSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.baseState}
          onChange={(e) => onUpdate({ ...data, baseState: e.target.value })}
          placeholder="Base state (starting point)"
          rows={3}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <textarea
          value={data.reflection?.prompt ?? ''}
          onChange={(e) => onUpdate({ ...data, reflection: { prompt: e.target.value } })}
          placeholder="Reflection prompt"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.layers.length} layer{data.layers.length === 1 ? '' : 's'}. Structural edits AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
